import { useEffect, useRef, useState } from 'react';
import { useApp } from '../context';
import { useAsync } from '../hooks';
import { Dismiss, Empty, ErrorNote, Loading, Modal, Panel, StateBadge } from '../ui';
import { closeTargetMonth, latestPublished, monthKey } from '../../lib/periods';
import { rememberSealed } from '../ledger';
import { dateLabel, money, monthLabel } from '../../lib/format';
import { navigate } from '../../lib/route';
import { errorMessage } from '../../data/repo';
import { processUpload, type ProcessOutcome } from '../../lib/process';
import type { Category, Month, ReviewItem, Upload } from '../../data/types';
import { suggestPayeeKey } from '../../lib/payee';
import { cleanCategoryName, customCategoryType, isAlreadyResolved, matchCategory } from '../../lib/review';
import { alertCheck, checkLine, needsRematch } from '../../lib/alertcheck';

const STATUS_TEXT: Record<Upload['processing_status'], string> = {
  uploaded: 'Waiting to process',
  processing: 'Processing',
  processed: 'Processed',
  failed: 'Failed',
  superseded: 'Duplicate copy',
};

export function Close() {
  const { repo, base, refresh } = useApp();
  const target = closeTargetMonth(base.months);
  const template = latestPublished(base.months) ?? base.openPlan;

  // Find or create the target month row, then set aside duplicate uploads. Both are idempotent.
  const month = useAsync(async (): Promise<Month | null> => {
    if (!target) return null;
    const m = await repo.ensureMonth(target, template);
    if (!repo.isDemo) await repo.supersedeDuplicates(m.id);
    return m;
  }, [target]);

  if (!target) {
    return (
      <Panel className="period-bar">
        <h2>Nothing to close</h2>
        <p className="muted">Every completed month is published. The current month can be closed once it ends.</p>
        <button className="secondary" onClick={() => navigate({ section: 'home' })}>Back to Home</button>
      </Panel>
    );
  }
  if (month.error) return <ErrorNote message={month.error} onRetry={month.reload} />;
  if (!month.data) return <Loading label={`Preparing ${monthLabel(target)}…`} />;
  return <CloseFlow month={month.data} onPublished={async () => { rememberSealed(monthKey(month.data!.month_start)); await refresh(); navigate({ section: 'home' }); }} key={month.data.id} />;
}

function CloseFlow({ month, onPublished }: { month: Month; onPublished: () => Promise<void> }) {
  const { repo, base, refresh } = useApp();
  const label = monthLabel(month.month_start);
  const uploads = useAsync(() => repo.uploads(month.id), [month.id]);
  const reviews = useAsync(() => repo.reviewItems(month.month_start), [month.id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [outcomes, setOutcomes] = useState<Record<string, ProcessOutcome>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [choice, setChoice] = useState<Record<string, string>>({});
  // Payee to remember per item; undefined = suggested default, '' = don't remember.
  const [payee, setPayee] = useState<Record<string, string>>({});
  const [remember, setRemember] = useState<Record<string, boolean>>({});
  const fileInput = useRef<HTMLInputElement>(null);
  const [sheet, setSheet] = useState<string | null>(null);
  // Errors from filing one item stay on that item's sheet, never on the Close card.
  const [sheetNotice, setSheetNotice] = useState<string | null>(null);
  const [outcomeNote, setOutcomeNote] = useState<string | null>(null);
  // Synchronous guard: a second tap before React re-renders must not file the same item twice.
  const filing = useRef(false);

  // Warn before leaving while a write is in flight.
  useEffect(() => {
    if (!busy) return;
    const on = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', on);
    return () => window.removeEventListener('beforeunload', on);
  }, [busy]);

  const all = uploads.data ?? [];
  const active = all.filter((u) => u.processing_status !== 'superseded');
  const superseded = all.length - active.length;
  const pending = active.filter((u) => u.processing_status === 'uploaded' || u.processing_status === 'failed' || u.processing_status === 'processing');
  const processed = active.filter((u) => u.processing_status === 'processed');
  const open = reviews.data ?? [];
  const reviewCats = base.categories;
  const sheetItem = open.find((r) => r.id === sheet) ?? null;

  const reloadAll = () => { uploads.reload(); reviews.reload(); };

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    setNotice(null);
    try {
      for (const [i, f] of [...files].entries()) {
        setBusy(`Uploading ${i + 1} of ${files.length}: ${f.name}`);
        await repo.uploadStatement(f, month);
      }
    } catch (e) {
      setNotice(errorMessage(e));
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
      reloadAll();
    }
  }

  async function processAll() {
    setNotice(null);
    const { parseStatementFile } = await import('../../lib/parser');
    const ctx = { monthStart: month.month_start, accounts: base.accounts, categories: base.categories, payeeRules: await repo.payeeRules() };
    for (const [i, u] of pending.entries()) {
      setBusy(`Processing ${i + 1} of ${pending.length}: ${u.original_filename}`);
      const out = await processUpload(repo, u, ctx, { parse: parseStatementFile });
      setOutcomes((o) => ({ ...o, [u.id]: out }));
      if (out.ok) ctx.categories = await repo.categories();
    }
    setBusy(null);
    await refresh();
    reloadAll();
  }

  function openSheet(id: string) {
    setSheetNotice(null);
    setSheet(id);
  }

  async function resolve(id: string): Promise<boolean> {
    if (filing.current) return false;
    const categoryId = choice[id];
    if (!categoryId) { setSheetNotice('Choose a category first.'); return false; }
    filing.current = true;
    setBusy('Saving');
    setSheetNotice(null);
    const item = open.find((r) => r.id === id);
    const key = remember[id] === false ? null : (payee[id] ?? (item ? suggestPayeeKey(item.description) : '')).trim() || null;
    try {
      const { alsoResolved } = await repo.resolveReview(id, categoryId, key);
      setOutcomeNote(key ? `Remembered “${key}”.${alsoResolved ? ` ${alsoResolved} more item${alsoResolved === 1 ? '' : 's'} for this payee resolved too.` : ''}` : null);
    } catch (e) {
      const message = errorMessage(e);
      // Already filed (a double tap, or a remembered payee got there first): the item is done.
      if (!isAlreadyResolved(message)) {
        setSheetNotice(message);
        reviews.reload();
        return false;
      }
      setOutcomeNote('That item was already filed.');
    } finally {
      filing.current = false;
      setBusy(null);
    }
    reviews.reload();
    return true;
  }

  /** Use a typed category: an existing one if the name already means it, otherwise a new one. */
  async function addCategory(id: string, typed: string): Promise<boolean> {
    const name = cleanCategoryName(typed);
    const item = open.find((r) => r.id === id);
    if (!name || !item) return false;
    const existing = matchCategory(name, base.categories);
    if (existing) { setChoice((c) => ({ ...c, [id]: existing.id })); return true; }
    setBusy('Adding category');
    setSheetNotice(null);
    try {
      const created = await repo.ensureCategory(name, customCategoryType(item.direction));
      await refresh();
      setChoice((c) => ({ ...c, [id]: created.id }));
      return true;
    } catch (e) {
      setSheetNotice(errorMessage(e));
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    setConfirming(false);
    setBusy('Publishing');
    setNotice(null);
    try {
      await repo.publishMonth(month.id);
      setBusy(null);
      await onPublished();
    } catch (e) {
      setBusy(null);
      setNotice(errorMessage(e));
      reloadAll();
      await refresh();
    }
  }

  const canProcess = pending.length > 0 && !busy && !repo.isDemo;
  const ready = processed.length > 0 && pending.length === 0 && open.length === 0;
  const step = !active.length ? 1 : pending.length ? 2 : open.length ? 3 : 4;
  const steps = ['Upload', 'Process', 'Review', 'Publish'];

  return (
    <div className="stack close">
      <Panel className="period-bar">
        <h2>Close {label} <StateBadge state={month.status === 'review' ? 'Review' : 'Open Plan'}>{month.status === 'review' ? 'In review' : 'Not started'}</StateBadge></h2>
        <p className="muted">Statements should cover {dateLabel(month.month_start)} to the last day of {label}.</p>
        <ol className="stepper">
          {steps.map((s, i) => <li key={s} className={i + 1 < step ? 'done' : i + 1 === step ? 'current' : ''}>{s}</li>)}
        </ol>
        {busy && <p className="busy" role="status">{busy}…</p>}
        {notice && <ErrorNote message={notice} />}
        {repo.isDemo && <p className="muted">Demo Mode is read-only. Close Month can be explored but nothing is saved.</p>}
      </Panel>

      <Panel title="Statements" meta={`${active.length} file${active.length === 1 ? '' : 's'}`}>
        {uploads.error ? <ErrorNote message={uploads.error} onRetry={uploads.reload} />
          : uploads.loading && !uploads.data ? <Loading />
          : active.length ? (
            <ul className="upload-list">
              {active.map((u) => {
                const o = outcomes[u.id];
                return (
                  <li key={u.id}>
                    <div>
                      <strong>{u.original_filename}</strong>
                      <span>Uploaded {dateLabel(u.uploaded_at)}{u.transaction_count !== null ? ` · ${u.transaction_count} transactions` : ''}</span>
                      {u.processing_status === 'failed' && u.processing_error && <span className="fail">{u.processing_error}</span>}
                      {o?.ok && <span>{o.bank} {o.currency}: {o.result.inserted} new, {o.result.duplicates} already recorded, {o.result.review_items} to review</span>}
                      {o?.ok && o.warnings.map((w) => <span className="warn" key={w}>{w}</span>)}
                    </div>
                    <span className={`ustatus ustatus-${u.processing_status}`}>{STATUS_TEXT[u.processing_status]}</span>
                  </li>
                );
              })}
            </ul>
          ) : <Empty>No statements uploaded for {label} yet.</Empty>}
        {superseded > 0 && <p className="muted">{superseded} duplicate cop{superseded === 1 ? 'y was' : 'ies were'} set aside. They are kept, not deleted, and are never counted.</p>}
        <div className="actions">
          <button className="secondary" disabled={Boolean(busy) || repo.isDemo} onClick={() => fileInput.current?.click()}>Add statements</button>
          <input ref={fileInput} type="file" accept="application/pdf,.pdf" multiple hidden onChange={(e) => void onFiles(e.target.files)} />
          <button className="cta" disabled={!canProcess} onClick={() => void processAll()}>
            {pending.length ? `Process ${pending.length} statement${pending.length === 1 ? '' : 's'}` : 'Nothing to process'}
          </button>
        </div>
      </Panel>

      <Panel title="Review" meta={`${open.length} open`}>
        {outcomeNote && <p className="status-line" role="status">{outcomeNote}</p>}
        {reviews.error ? <ErrorNote message={reviews.error} onRetry={reviews.reload} />
          : reviews.loading && !reviews.data ? <Loading />
          : open.length ? (
            <>
              <p className="muted small">Tap an item to file it. Payees you remember are filed automatically from next month.</p>
              <ul className="review-list">
                {open.map((r) => (
                  <li key={r.id}>
                    <button className="review-item" onClick={() => openSheet(r.id)}>
                      <div>
                        <strong>{r.description}</strong>
                        <span>{dateLabel(r.tx_date)}{r.question?.startsWith('Same amount') ? ', possible transfer between your accounts' : ''}</span>
                      </div>
                      <b>{r.direction === 'credit' ? '+' : '−'}{money(r.amount, r.currency)}</b>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : <Empty>{processed.length ? 'Nothing needs review.' : 'Items appear here only if a material transaction cannot be placed.'}</Empty>}
      </Panel>
      {sheetItem && (
        <ReviewSheet item={sheetItem} categories={reviewCats} busy={Boolean(busy)}
          choice={choice[sheetItem.id] ?? ''} onChoose={(id) => setChoice((c) => ({ ...c, [sheetItem.id]: id }))}
          remember={remember[sheetItem.id] !== false} onRemember={(v) => setRemember((m) => ({ ...m, [sheetItem.id]: v }))}
          payee={payee[sheetItem.id] ?? suggestPayeeKey(sheetItem.description)} onPayee={(v) => setPayee((m) => ({ ...m, [sheetItem.id]: v.toUpperCase() }))}
          notice={sheetNotice} canAdd={!repo.isDemo}
          onAdd={(name) => addCategory(sheetItem.id, name)}
          onClose={() => setSheet(null)}
          onConfirm={async () => { const ok = await resolve(sheetItem.id); if (ok) setSheet(null); }} />
      )}

      <AlertsCheck month={month} uploads={all} />

      <Panel title="Publish" meta="Creates the official month">
        <p className="muted">
          {!active.length ? 'Upload this month’s statements first.'
            : pending.length ? `${pending.length} statement${pending.length === 1 ? '' : 's'} still to process.`
            : open.length ? `${open.length} item${open.length === 1 ? '' : 's'} to review first.`
            : `All checks pass. Publishing locks ${label} and makes it your latest published month.`}
        </p>
        {confirming ? (
          <div className="confirm" role="alertdialog" aria-labelledby="confirm-publish">
            <p id="confirm-publish"><b>Publish {label}?</b> Once published it is locked and cannot be edited.</p>
            <div className="confirm-actions">
              <button className="secondary" onClick={() => setConfirming(false)}>Not yet</button>
              <button className="cta" autoFocus onClick={() => void publish()}>Publish and lock</button>
            </div>
          </div>
        ) : (
          <button className="cta" disabled={!ready || Boolean(busy) || repo.isDemo} onClick={() => setConfirming(true)}>Publish {label}</button>
        )}
      </Panel>
    </div>
  );
}

/** One review item as a sheet: the amount, what it is, and the likely categories as one-tap chips. */
function ReviewSheet({ item, categories, busy, choice, onChoose, remember, onRemember, payee, onPayee, notice, canAdd, onAdd, onClose, onConfirm }: {
  item: ReviewItem; categories: Category[]; busy: boolean; choice: string; onChoose: (id: string) => void;
  remember: boolean; onRemember: (v: boolean) => void; payee: string; onPayee: (v: string) => void;
  notice: string | null; canAdd: boolean; onAdd: (name: string) => Promise<boolean>; onClose: () => void; onConfirm: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [typed, setTyped] = useState('');
  async function add() {
    if (!cleanCategoryName(typed) || busy) return;
    if (await onAdd(typed)) { setAdding(false); setTyped(''); }
  }
  const transfer = item.question?.startsWith('Same amount') ?? false;
  const active = categories.filter((c) => c.is_active);
  const byName = (n: string) => active.find((c) => c.name.toLowerCase() === n.toLowerCase());
  const suggested = [item.classification ? byName(item.classification) : undefined, transfer ? byName('Internal Transfer') : undefined]
    .filter((c): c is Category => Boolean(c));
  const everyday = active.filter((c) => ['lifestyle', 'fixed', 'exceptional'].includes(c.category_type) && !suggested.includes(c))
    .sort((a, b) => a.name.localeCompare(b.name)).slice(0, 14);
  // A chosen category outside the quick set (picked from More, or just typed) shows as its own chip.
  const picked = active.find((c) => c.id === choice);
  const quick = [...suggested, ...everyday];
  if (picked && !quick.includes(picked)) quick.push(picked);
  const rest = active.filter((c) => !quick.includes(c)).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Modal title={item.direction === 'credit' ? 'Money in' : 'Money out'} kicker={dateLabel(item.tx_date)} onClose={onClose}
      footer={<>
        <Dismiss>Later</Dismiss>
        <button className="cta" disabled={!choice || busy} onClick={onConfirm}>File it</button>
      </>}>
      <p className="sheet-amount">{item.direction === 'credit' ? '+' : '−'}{money(item.amount, item.currency)}</p>
      <p className="sheet-desc">{item.description}</p>
      {item.question && transfer && <p className="sheet-q">{item.question}</p>}
      <div className="cat-chips" role="radiogroup" aria-label="Category">
        {quick.map((c) => (
          <button key={c.id} type="button" role="radio" aria-checked={choice === c.id}
            className={`chip ${choice === c.id ? 'active' : ''} ${suggested.includes(c) ? 'suggested' : ''}`} onClick={() => onChoose(c.id)}>
            {c.name}
          </button>
        ))}
        {rest.length > 0 && (
          <select className="chip" aria-label="Other categories" value=""
            onChange={(e) => e.target.value && onChoose(e.target.value)}>
            <option value="">More</option>
            {rest.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        {canAdd && !adding && (
          <button type="button" className="chip chip-new" onClick={() => setAdding(true)}>New category</button>
        )}
      </div>
      {adding && (
        <form className="new-cat" onSubmit={(e) => { e.preventDefault(); void add(); }}>
          <input type="text" aria-label="New category name" placeholder="e.g. Electronics" maxLength={40} autoFocus
            value={typed} onChange={(e) => setTyped(e.target.value)} />
          <button type="submit" className="secondary" disabled={!cleanCategoryName(typed) || busy}>Add</button>
          <button type="button" className="link" onClick={() => { setAdding(false); setTyped(''); }}>Cancel</button>
        </form>
      )}
      <label className="remember">
        <input type="checkbox" checked={remember} onChange={(e) => onRemember(e.target.checked)} />
        <span>Remember this payee as</span>
        <input type="text" aria-label="Payee to remember" disabled={!remember} value={payee} onChange={(e) => onPayee(e.target.value)} />
      </label>
      {notice && <ErrorNote message={notice} />}
    </Modal>
  );
}

/**
 * Bank alerts against the processed statements: which alerts each statement does not contain.
 * A cross-check, never a blocker: statements are the record (DECISIONS 26).
 */
function AlertsCheck({ month, uploads }: { month: Month; uploads: Upload[] }) {
  const { repo, base } = useApp();
  const processedKey = uploads.filter((u) => u.processing_status === 'processed').map((u) => u.id).join(',');
  const rows = useAsync(async () => {
    let r = await repo.provisional(month.month_start);
    // Alerts that arrived after a statement was processed have not been compared yet: compare them now.
    const again = r && !repo.isDemo ? needsRematch(r, uploads) : [];
    if (again.length) {
      for (const id of again) await repo.matchAlerts(id);
      r = await repo.provisional(month.month_start);
    }
    return r;
  }, [month.id, processedKey]);
  if (rows.error) return <ErrorNote message={rows.error} onRetry={rows.reload} />;
  if (!rows.data?.length) return null;
  const checks = alertCheck(rows.data, uploads, base.accounts);
  const missing = checks.reduce((n, c) => n + c.unmatched.length, 0);
  return (
    <Panel title="Bank alerts check" meta={missing ? `${missing} not on a statement` : 'For your eyes'}>
      <p className="muted small">Each alert is looked for on its account's statement. One that is not there usually means a missing statement page, a card hold that was released, or a reversal. It does not stop you publishing.</p>
      <ul className="tx-list">
        {checks.map((c) => (
          <li key={c.accountId}>
            <div>
              <strong>{c.label}</strong>
              <span>{checkLine(c)}</span>
            </div>
          </li>
        ))}
      </ul>
      {missing > 0 && (
        <>
          <h3 className="sub-head">Not on a statement</h3>
          <ul className="tx-list">
            {checks.flatMap((c) => c.unmatched.map((r) => (
              <li key={r.id}>
                <div>
                  <strong>{r.description}</strong>
                  <span>{dateLabel(r.tx_date)} · {c.label}</span>
                </div>
                <b className={r.direction === 'credit' ? 'credit' : ''}>{r.direction === 'credit' ? '+' : '−'}{money(r.amount, r.currency)}</b>
              </li>
            )))}
          </ul>
        </>
      )}
    </Panel>
  );
}
